import { supabase } from '../lib/supabase';

export const dashboardService = {
  async getDashboardStats(userId) {
    // CRITICAL FIX: Validate userId before making database calls
    if (!userId || userId === 'undefined' || userId === 'null') {
      console.error('Invalid userId provided to getDashboardStats:', userId);
      throw new Error('User ID is required to fetch dashboard stats');
    }

    try {
      // Get user profile with wallet balance - use maybeSingle() to handle missing profile
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('wallet_balance, total_sales, total_purchases')
        ?.eq('id', userId)
        ?.maybeSingle();
      
      // Only throw error if it's NOT a "no rows" error
      if (profileError && profileError?.code !== 'PGRST116') {
        throw profileError;
      }

      // If no profile exists, return default stats
      if (!profile) {
        console.warn('No user profile found for userId:', userId);
        return {
          walletBalance: 0,
          totalSales: 0,
          totalPurchases: 0,
          activeVouchers: 0,
          soldVouchers: 0,
          purchasedVouchers: 0
        };
      }

      // Get active vouchers count (user's active listings)
      const { count: activeVouchers, error: vouchersError } = await supabase?.from('vouchers')?.select('*', { count: 'exact', head: true })?.eq('seller_id', userId)?.eq('voucher_status', 'active');
      
      if (vouchersError) throw vouchersError;

      // Get sold vouchers count
      const { count: soldVouchers, error: soldError } = await supabase?.from('transactions')?.select('*', { count: 'exact', head: true })?.eq('seller_id', userId)?.eq('transaction_status', 'completed');
      
      if (soldError) throw soldError;

      // Get purchased vouchers count
      const { count: purchasedVouchers, error: purchasedError } = await supabase?.from('transactions')?.select('*', { count: 'exact', head: true })?.eq('buyer_id', userId)?.eq('transaction_status', 'completed');
      
      if (purchasedError) throw purchasedError;

      return {
        walletBalance: profile?.wallet_balance || 0,
        totalSales: profile?.total_sales || 0,
        totalPurchases: profile?.total_purchases || 0,
        activeVouchers: activeVouchers || 0,
        soldVouchers: soldVouchers || 0,
        purchasedVouchers: purchasedVouchers || 0
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  async getRecentTransactions(userId, limit = 5) {
    // CRITICAL FIX: Validate userId before making database calls
    if (!userId || userId === 'undefined' || userId === 'null') {
      console.error('Invalid userId provided to getRecentTransactions:', userId);
      throw new Error('User ID is required to fetch transactions');
    }

    try {
      const { data, error } = await supabase
        ?.from('transactions')
        ?.select(`
          *,
          voucher:vouchers(id, title, company_name),
          buyer:user_profiles!buyer_id(id, full_name),
          seller:user_profiles!seller_id(id, full_name)
        `)
        ?.or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
        ?.order('transaction_date', { ascending: false })
        ?.limit(limit);
      
      if (error) throw error;
      
      return data?.map(txn => ({
        id: txn?.id,
        voucherId: txn?.voucher_id,
        buyerId: txn?.buyer_id,
        sellerId: txn?.seller_id,
        amount: txn?.amount,
        transactionStatus: txn?.transaction_status,
        paymentMethod: txn?.payment_method,
        transactionDate: txn?.transaction_date,
        completedDate: txn?.completed_date,
        notes: txn?.notes,
        voucher: txn?.voucher ? {
          id: txn?.voucher?.id,
          title: txn?.voucher?.title,
          companyName: txn?.voucher?.company_name
        } : null,
        buyer: txn?.buyer ? {
          id: txn?.buyer?.id,
          fullName: txn?.buyer?.full_name
        } : null,
        seller: txn?.seller ? {
          id: txn?.seller?.id,
          fullName: txn?.seller?.full_name
        } : null,
        isSeller: txn?.seller_id === userId
      })) || [];
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      throw error;
    }
  }
};